/**
 * Global Error Monitor
 *
 * Captures and logs:
 * - Unhandled promise rejections
 * - React Query errors
 * - Network failures
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { logBuffer } from '@/services/api/log-buffer.service';

export function GlobalErrorMonitor() {
  const queryClient = useQueryClient();

  useEffect(() => {
    // Monitor unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;

      logBuffer.addErrorLog({
        error_type: 'UnhandledRejection',
        message: error instanceof Error ? error.message : String(error),
        stack_trace: error instanceof Error ? error.stack : undefined,
        page_url: window.location.pathname,
        severity: 'high',
        context: {
          source: 'unhandled_promise_rejection',
        },
      });

      console.error('[ErrorMonitor] Unhandled promise rejection:', error);
    };

    // Monitor React Query errors globally
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        const query = event.query;
        const error = query.state.error;

        if (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);

          // Determine severity based on error type
          let severity: 'low' | 'medium' | 'high' | 'critical' = 'medium';

          if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
            severity = 'high';
          } else if (errorMessage.includes('unauthorized') || errorMessage.includes('forbidden')) {
            severity = 'high';
          } else if (errorMessage.includes('not found')) {
            severity = 'low';
          }

          logBuffer.addErrorLog({
            error_type: 'ReactQueryError',
            message: errorMessage,
            stack_trace: error instanceof Error ? error.stack : undefined,
            page_url: window.location.pathname,
            severity,
            context: {
              queryKey: query.queryKey,
              source: 'react_query',
            },
          });

          if (import.meta.env.DEV) {
            console.warn('[ErrorMonitor] React Query error:', {
              queryKey: query.queryKey,
              error: errorMessage,
            });
          }
        }
      }
    });

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      unsubscribe();
    };
  }, [queryClient]);

  return null; // This component doesn't render anything
}
